/**
 * Chamadas HTTP da tela de usuários.
 *
 * Papel: `buscarUsuarios` lê uma página de `GET /api/usuarios` e
 * `salvarPermissoes` grava a grade de uma pessoa em
 * `PUT /api/usuarios/{id}/permissoes`.
 *
 * Como usar: as duas rotas respondem 403 a quem não tem o papel
 * `concierge:admin`, e é esse 403, e não a `RotaDeAdministrador`, que protege os
 * dados. Nenhuma transformação acontece aqui — o que sai é o que o schema
 * validou —, de modo que regra de tela colocada neste arquivo deixaria de valer
 * para quem chamar o service de outro lugar.
 */
import {
  type DefinirPermissoes,
  type ParametrosListagem,
  permissoesSalvasSchema,
  respostaPaginadaSchema,
  usuarioSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/** Envelope paginado da listagem de usuários. */
const listaDeUsuariosSchema = respostaPaginadaSchema(usuarioSchema);

/**
 * Busca uma página de usuários com conta.
 *
 * A busca procura no nome e no e-mail. Quem nunca entrou no Concierge não aparece,
 * porque a linha em `usuarios` só nasce na primeira consulta a `/api/sessao`.
 *
 * @param parametros - `pagina` a partir de 1, `porPagina` até 200 e `busca`
 *   opcional; `busca` vazia ou ausente lista todos.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query, para a
 *   página abandonada não sobrescrever a que a pessoa acabou de pedir.
 * @returns A página, no formato de `respostaPaginadaSchema(usuarioSchema)`.
 */
export function buscarUsuarios(parametros: ParametrosListagem, sinal?: AbortSignal) {
  return clienteApi.get('/usuarios', {
    schema: listaDeUsuariosSchema,
    params: parametros,
    sinal,
  });
}

/**
 * Substitui o conjunto inteiro de permissões de uma pessoa.
 *
 * Módulo fora da lista perde o acesso. Enviar só a linha alterada, e não a grade
 * inteira, revogaria todos os outros módulos da pessoa.
 *
 * @param id - Identificador da linha em `usuarios`, e não o `auth0Sub`.
 * @param dados - O conjunto que passa a valer, sem módulo repetido e sem
 *   `usuarios`, que `definirPermissoesSchema` recusa.
 * @returns O conjunto como ficou gravado.
 */
export function salvarPermissoes(id: string, dados: DefinirPermissoes) {
  return clienteApi.put(`/usuarios/${encodeURIComponent(id)}/permissoes`, dados, {
    schema: permissoesSalvasSchema,
  });
}
